import React, {Suspense} from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3';
import configureStore from './configureStore';
import configureStoreProd from './configureStoreProd';
import {BrowserRouter as Router} from "react-router-dom";
import * as serviceWorker from './serviceWorker';
import flagsmith from 'flagsmith';
import { v4 as uuidv4 } from 'uuid';
import _ from "underscore";

// Never remove this line async-await
import regeneratorRuntime from "regenerator-runtime";
import { deleteCookie, detectPlatformChange, getCookie, setCookie } from "./components/common/utils"
import { datadogLogs } from '@datadog/browser-logs';

import config from "./config";
import {
    getHomeDataDetails,
    getMobileBp,
    getPromoMessages,
    userLoggedIn
} from "./components/actionCreators/appAC"
import {
    getStoreAvailableCountries,
    openShopifyCart
} from "./components/actionCreators/shopifyAC";
import { getCartAndRedirectToCheckout, setShopifyCart } from "./components/actionCreators/cart"
import {authCheck, clearTradeItemsFromSession} from "./components/common/utils";
import axios from "axios";
import {setUserProfile} from "./components/actionCreators/myAccountAC";
import PdpSliders from "./components/PDP/PdpSliders";
import Breadcrumbs from "./components/PDP/Breadcrumbs";
import appErrorLogger from "./components/common/DataDogErrorLog";
import appInfoLogger from "./components/common/DataDogInfoLog";
import ConfirmCredentials from "./components/ConfirmCredentials";
import GbTracker from "gb-tracker-client"
import ScrollToTop from "./components/common/ScrollToTop";

const store = process.env.REACT_APP_STAGE !== "dev" ? configureStoreProd(): configureStore();

// regex patterns to identify known bot instances:
let botPattern = "(googlebot\/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)";
let regex = new RegExp(botPattern, 'i');
// define var conditionalSampleRate as 0 if the userAgent matches a pattern in botPatterns
// otherwise, define conditionalSampleRate as 100
let conditionalSampleRate = regex.test(navigator.userAgent) ? 0 : 100

const auth_email = getCookie('auth_email') || 'guest';
datadogLogs.init({
    clientToken: 'pub9ac1dbd3a7055e7835dc8c9f4d6e80aa',
    site: 'datadoghq.com',
    version: process.env.DD_VERSION || "2021102101",
    env: process.env.REACT_APP_STAGE || 'staging',
    service: 'react-rebag-fe',
    forwardErrorsToLogs: true,
    sampleRate: conditionalSampleRate,
    beforeSend: (log) => {
        let must_be_recorded = true;
        if(log.http && log.http.url && log.http.url.indexOf('rebag.com') === -1){
            must_be_recorded = false;
        }
        return must_be_recorded;
    },
})
datadogLogs.setLoggerGlobalContext({
    email: auth_email
});
datadogLogs.logger.setHandler(process.env.REACT_APP_STAGE === "prod" ? 'http' : 'console');

window.appErrorLogger = appErrorLogger;
window.appInfoLogger = appInfoLogger;

Date.prototype.addHours= function(h){
    this.setHours(this.getHours()+h);
    return this;
}

let app_mode = !!getCookie("app_mode");
const url_search_params = new URLSearchParams(window.location.search)
if(url_search_params.get("platform") === "native"){
    setCookie("app_mode", "true", 365)
    document.getElementsByTagName("html")[0].classList.add("app_mode");
    app_mode = true;
}
if(url_search_params.get('gclid')){
    setCookie('gclid', url_search_params.get('gclid'), 1)
}

if(url_search_params.get('ttclid')){
    setCookie('ttclid', url_search_params.get('ttclid'), 1)
}

const session_cache = process.env.REACT_APP_STAGE === "dev" ? 0 : 1; // in hrs
const getInitialData = async () => {
    const user_release_version = localStorage.getItem('release_version');
    const star_date = localStorage.getItem("star_date");
    const now_date = Date.now();

    const use_cache = !!localStorage.getItem("init")
        && !!star_date
        && new Date(parseInt(star_date)).getTime() > now_date
        && !!user_release_version
        && user_release_version === config.release_version
    ;

    //add safe user cookie if query param exist
    if(url_search_params.get('TAkkSnIQO4M8g5')){
        setCookie('TAkkSnIQO4M8g5', 'b0dk3LX0zIcp40', 365);
        url_search_params.delete('TAkkSnIQO4M8g5');
    }

    if(use_cache){
        return JSON.parse(localStorage.getItem("init"));
    } else {
        const next_date = new Date().addHours(session_cache).getTime();
        return await axios.get(`${config.host_rebag_api}/v6/initial-data/?timestamp=${new Date().getTime()}`)
            .then((response) => {
                const country_code = {
                    country_code: response.headers["x-country-code"] || "country_unknown"
                };

                const init = {...response.data, ...country_code, ...config};

                localStorage.setItem("country_code", response.headers["x-country-code"]);

                localStorage.setItem("init", JSON.stringify(init));
                localStorage.setItem("star_date", JSON.stringify(next_date));
                localStorage.setItem("release_version", config.release_version);
                return init;
            })
            .catch(function (error) {
                appErrorLogger('Index: getInitialData', error);
            });
    }
}

getInitialData()
    .then((init) => {
        const queryParams = new URLSearchParams(window.location.search);
        const isGoogleLogin = queryParams.get('redirect_back_google');
        const urlBeforeGoogleLogin = getCookie('urlBeforeGoogleLogin');
        if (isGoogleLogin && urlBeforeGoogleLogin) {
            deleteCookie('urlBeforeGoogleLogin');
            if (urlBeforeGoogleLogin.indexOf('/sell-now') !== -1) {
                window.location.href = urlBeforeGoogleLogin.split('sell-now')[0] + 'sell-now';
            } else {
                window.location.href = urlBeforeGoogleLogin;
            }
        } else {
            if (getCookie('clickedGoogleLogin')) {
                if (getCookie('new_google_account')) {
                    window.dataLayer.push({
                        'event': 'signupGoogle',
                    });
                } else {
                    window.dataLayer.push({
                        'event': 'loginGoogle',
                    });
                }
                deleteCookie("clickedGoogleLogin");
            }
        }
        store.dispatch({
            type: "INIT",
            init: init
        });
    })
    .then(() => {
       return store.dispatch(setUserProfile());
    })
    .then(() => {
       return store.dispatch(getStoreAvailableCountries());
    })
    .then(() => {
        const {user_profile} = store.getState().myAccountReducer;
        const {isInternational, isUsdInternational} = store.getState().appReducer;
        const gbTracker = new GbTracker('rebag', 'ProductionRb');
        gbTracker.autoSetVisitor(user_profile ? `${user_profile.shopify_id}`: '');
        window.gbTracker = gbTracker;
        let flagsmithIdentity = null;
        const flagsmithCookie = getCookie('flagsmithIdentity');
        if (flagsmithCookie) {
            flagsmithIdentity = flagsmithCookie;
        } else {
            flagsmithIdentity = uuidv4();
            setCookie('flagsmithIdentity', flagsmithIdentity, 30);
        }

        flagsmith.init({
            environmentID: config.flagsmith_key,
            identity: flagsmithIdentity,
            enableAnalytics: true,
            traits: {
                email_address:  user_profile ? user_profile.email : null,
                personalization_features:  user_profile ? user_profile.personalization_features : false,
                non_usd_international: isInternational ? !isUsdInternational : false,
            }, // these will add to the user's existing traits
            onChange: (oldFlags, params) => {
                window.dataLayer.push({
                    'event': 'flagsmith_identify',
                    'personalization_enabled': user_profile && user_profile.personalization_features ? "enabled" : "not_qualified",
                })
            },
        });

    })
    .then(() => {
        return store.dispatch(getCartAndRedirectToCheckout());
    })
    .then(() => {
        const throttledResize = _.throttle(() => {
            store.dispatch(getMobileBp());
        }, 200);
        store.dispatch(getMobileBp());
        store.dispatch(getPromoMessages());
        store.dispatch(setShopifyCart());
        detectPlatformChange();
        window.addEventListener('resize', throttledResize);

        if (authCheck()) {
            store.dispatch(userLoggedIn());
        } else {
            sessionStorage.removeItem('checkout_validated');
            clearTradeItemsFromSession();
        }

        store.dispatch(getHomeDataDetails());

        const page_location = window.location.href;
        if(page_location.search("cart=open") !== -1){
            store.dispatch(openShopifyCart(false));
        }
    })
    .then(() => {
      store.dispatch({type: "HIDE_SPINNER"});
    })
    .then(() => {
        const rbg_checkout_ty = document.getElementById('custom-checkout-cart-ty');

        const pdp = document.getElementById('buying-section-pdp');

        const pdpSliders = document.getElementById('buying-section-product-sliders');

        const plp = document.getElementById('buying-section-plp');

        const breadcrumbs = document.getElementById('mob-breadcrumbs');

        const financing = document.getElementById('react-financing');

        const style_assembly = document.getElementById('react-style-assembly');

        const vault_homepage_new_arrivals = document.getElementById('react-vault-homepage-new-arrivals');

        const vault_homepage_trending = document.getElementById('react-vault-homepage-trending');

        const vault_custom_slider = document.getElementById('react-vault-custom-slider');

        const clair_report_2020 = document.getElementById('react-clair-report-2020');

        const clair_report_2021 = document.getElementById('react-clair-report-2021');

        const clair_report_2022 = document.getElementById('react-clair-report-2022');

        const clair_report_2023 = document.getElementById('react-clair-report-2023');

        const clair_report_2024 = document.getElementById('react-clair-report-2024');

        const trendlee = document.getElementById('react-trendlee');

        const rebag_certified = document.getElementById('react-rebag-certified');

        const rebag_stylist = document.getElementById('react-rebag-stylist');

        const clair_trade = document.getElementById('react-clair-trade');

        const about_clair = document.getElementById('react-about_clair');

        const about_us = document.getElementById('react-about-us');

        const react_rewards = document.getElementById('react-rewards');

        const react_new_homepage = document.getElementById('react-new-homepage');

        const offer_coupon = document.getElementById('react-offer-coupon');

        const react_footer = document.getElementById('react-footer');

        const react_header = document.getElementById('react-header');

        const gift_guide = document.getElementById('react-gift-guide');

        const react_wholesale = document.getElementById('react-wholesale');

        const homepage = document.getElementById('react-homepage');
        if(homepage){
            const Homepage = React.lazy(() => import('./components/Homepage' /* webpackChunkName: "homepage" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router>
                            <Homepage />
                        </Router>
                    </Suspense>
                </Provider>,
                homepage
            );
        }

        if(clair_report_2020){
            const ClairReport2020 = React.lazy(() => import('./components/ClairReport2020' /* webpackChunkName: "clair_report_2020" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <ClairReport2020 />
                    </Suspense>
                </Provider>,
                clair_report_2020
            );
        }

        if(clair_report_2021){
            const ClairReport2021 = React.lazy(() => import('./components/ClairReport2021' /* webpackChunkName: "clair_report_2021" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router basename="/clair-report/2021">
                            <ClairReport2021 />
                        </Router>
                    </Suspense>
                </Provider>,
                clair_report_2021
            );
        }

        if(clair_report_2022){
            const ClairReport2022 = React.lazy(() => import('./components/ClairReport2022' /* webpackChunkName: "clair_report_2022" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router basename="/clair-report/2022">
                            <ClairReport2022 />
                        </Router>
                    </Suspense>
                </Provider>,
                clair_report_2022
            );
        }

        if(clair_report_2023){
            const ClairReport2023 = React.lazy(() => import('./components/ClairReport2023' /* webpackChunkName: "clair_report_2023" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router basename="/clair-report/2023">
                            <ClairReport2023 />
                        </Router>
                    </Suspense>
                </Provider>,
                clair_report_2023
            );
        }

        if(clair_report_2024){
            const ClairReport2024 = React.lazy(() => import('./components/ClairReport2024' /* webpackChunkName: "clair_report_2024" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <ClairReport2024 />
                    </Suspense>
                </Provider>,
                clair_report_2024
            );
        }

        if(trendlee){
            const Trendlee = React.lazy(() => import('./components/Trendlee/' /* webpackChunkName: "trendlee" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Trendlee />
                    </Suspense>
                </Provider>,
                trendlee
            );
        }

        if(rebag_certified){
            const RebagCertified = React.lazy(() => import('./components/RebagCertified' /* webpackChunkName: "rebag_certified" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <RebagCertified />
                    </Suspense>
                </Provider>,
                rebag_certified
            );
        }

        if(about_clair){
            const AboutClair = React.lazy(() => import('./components/AboutClair' /* webpackChunkName: "about_clair" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <AboutClair />
                    </Suspense>
                </Provider>,
                about_clair
            );
        }

        if(about_us){
            const AboutUs = React.lazy(() => import('./components/AboutUs' /* webpackChunkName: "about_us" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <AboutUs />
                    </Suspense>
                </Provider>,
                about_us
            );
        }

        if(react_rewards){
            const Reward = React.lazy(() => import('./components/Reward' /* webpackChunkName: "reward" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Reward />
                    </Suspense>
                </Provider>,
                react_rewards
            );
        }

        if(react_new_homepage){
            const NewHomepage = React.lazy(() => import('./components/NewHomepage' /* webpackChunkName: "newhomepage" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <NewHomepage />
                    </Suspense>
                </Provider>,
                react_new_homepage
            );
        }

        if(gift_guide){
            const GiftGuide2024 = React.lazy(() => import('./components/GiftGuide2024' /* webpackChunkName: "gift_guide" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <GiftGuide2024 />
                    </Suspense>
                </Provider>,
                gift_guide
            );
        }

        if(react_wholesale){
            const Wholesale = React.lazy(() => import('./components/Wholesale' /* webpackChunkName: "wholesale" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Wholesale />
                    </Suspense>
                </Provider>,
                react_wholesale
            );
        }

        if(react_header || react_footer){
            const Common = React.lazy(() => import('./Common' /* webpackChunkName: "common" */));
            if (react_header && !clair_report_2021 && !clair_report_2022) {
                ReactDOM.render(
                    <Provider store={store}>
                        <Suspense fallback={<div></div>}>
                            <GoogleReCaptchaProvider
                                reCaptchaKey={config.gc_key}
                            >
                                <Common
                                    react_header={!app_mode}
                                    react_footer={false}
                                />
                            </GoogleReCaptchaProvider>
                        </Suspense>
                    </Provider>,
                    document.getElementById('react-header')
                );
            }
            if (react_footer) {
                ReactDOM.render(
                    <Provider store={store}>
                        <Suspense fallback={<div></div>}>
                            <Common
                                react_header={false}
                                react_footer={!app_mode}
                            />
                        </Suspense>
                    </Provider>,
                    document.getElementById('react-footer')
                );
            }
        }

        const clair_offer = document.getElementById('clair-offer');
        const SellNow = React.lazy(() => import('./SellNow' /* webpackChunkName: "sell_now" */));
        if (clair_offer){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router basename="/sell-now">
                            <ScrollToTop />
                            <SellNow
                              isTradeIn={false}
                            />
                        </Router>
                    </Suspense>
                </Provider>,
                clair_offer
            )
        }

        const clair_trade_in = document.getElementById('clair-trade-in');
        if (clair_trade_in){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router basename="/trade-in">
                            <SellNow
                              isTradeIn={true}
                            />
                        </Router>
                    </Suspense>
                </Provider>,
                clair_trade_in
            )
        }

        const my_account = document.getElementById('my-account');
        const MyAccount = React.lazy(() => import('./MyAccount' /* webpackChunkName: "my_account" */));
        if (my_account){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router basename="/my-account">
                            <ScrollToTop />
                            <MyAccount />
                        </Router>
                    </Suspense>
                </Provider>,
                my_account
            );
        }

        const rbg_cart = document.getElementById('rbg_cart');
        const CartContainer = React.lazy(() => import('./CartContainer' /* webpackChunkName: "cart" */));
        if(rbg_cart){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <CartContainer />
                        <ConfirmCredentials />
                    </Suspense>
                </Provider>,
                rbg_cart
            );
        }

        if(pdpSliders){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <PdpSliders />
                    </Suspense>
                </Provider>,
                pdpSliders
            );
        }
        if(breadcrumbs){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Breadcrumbs

                        />
                    </Suspense>
                </Provider>,
                breadcrumbs
            );
        }
        let renderElement = false;
        let vaultRenderElement = false;
        if (vault_homepage_trending) renderElement = vault_homepage_trending;
        if (vault_homepage_new_arrivals) vaultRenderElement = vault_homepage_new_arrivals;
        if (vault_custom_slider) renderElement = vault_custom_slider;
        if (financing) renderElement = financing;
        if (style_assembly) renderElement = style_assembly;
        if (pdp) renderElement = pdp;
        if (plp) renderElement = plp;
        if (rbg_checkout_ty) renderElement = rbg_checkout_ty;
        if (rebag_stylist) renderElement = rebag_stylist;
        if (clair_trade) renderElement = clair_trade;
        if (offer_coupon) renderElement = offer_coupon;

        const All = React.lazy(() => import('./All' /* webpackChunkName: "all" */));
        if (pdp) {
            ReactDOM.render(
              <Provider store={store}>
                  <Suspense fallback={<div></div>}>
                      <All pdp={true} />
                  </Suspense>
              </Provider>,
              pdp
            );
        } else if (renderElement) {
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <All
                            vault_homepage_trending={vault_homepage_trending === renderElement}
                            vault_custom_slider={vault_custom_slider === renderElement}
                            financing={financing === renderElement}
                            style_assembly={style_assembly === renderElement}
                            plp={plp === renderElement}
                            rebag_stylist={rebag_stylist === renderElement}
                            clair_trade={clair_trade === renderElement}
                            offer_coupon={offer_coupon === renderElement}
                            renderElement={renderElement}
                        />
                    </Suspense>
                </Provider>,
                renderElement
            );
        }
        if(vaultRenderElement){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <All
                            vault_homepage_new_arrivals={vault_homepage_new_arrivals === vaultRenderElement}
                        />
                    </Suspense>
                </Provider>,
                vaultRenderElement
            );
        }

        const wallet_lp = document.getElementById('react-wallet-lp');
        const WalletLP = React.lazy(() => import('./components/WalletLP/' /* webpackChunkName: "wallet_lp" */));
        if(wallet_lp){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <WalletLP />
                    </Suspense>
                </Provider>,
                wallet_lp
            )
        }

        const mostwanted_lp = document.getElementById('react-most-wanted-lp');
        const MostWantedLP = React.lazy(() => import('./components/MostWantedLP/' /* webpackChunkName: "wallet_lp" */));
        if(mostwanted_lp){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <MostWantedLP />
                    </Suspense>
                </Provider>,
                mostwanted_lp
            )
        }

        const consignment_lp = document.getElementById('react-consignment-lp');
        const ConsignmentLP = React.lazy(() => import('./components/ConsignmentLP/' /* webpackChunkName: "consignment_lp" */));
        if(consignment_lp){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <ConsignmentLP />
                    </Suspense>
                </Provider>,
                consignment_lp
            )
        }

        const consignment_sell_lp = document.getElementById('react-consignment-sell-lp');
        const ConsignmentSellLP = React.lazy(() => import('./components/ConsignmentSellLP/' /* webpackChunkName: "consignment_sell_lp" */));
        if(consignment_sell_lp){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <ConsignmentSellLP />
                    </Suspense>
                </Provider>,
                consignment_sell_lp
            )
        }

        const consignment_buyout_lp = document.getElementById('react-consignment-buyout-lp');
        const ConsignmentBuyoutLP = React.lazy(() => import('./components/ConsignmentBuyoutLP/' /* webpackChunkName: "consignment_buyout_lp" */));
        if(consignment_buyout_lp){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <ConsignmentBuyoutLP />
                    </Suspense>
                </Provider>,
                consignment_buyout_lp
            )
        }

        const rewards_plus_join = document.getElementById('react-rewards-plus');
        const RewardsPlusJoin = React.lazy(() => import('./components/RewardsPlusJoin/' /* webpackChunkName: "rewards_plus_join" */));
        if(rewards_plus_join){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <RewardsPlusJoin />
                    </Suspense>
                </Provider>,
                rewards_plus_join
            )
        }

        const rewards_plus_lp = document.getElementById('react-rewards-plus-lp');
        const RewardsPlusLP = React.lazy(() => import('./components/RewardsPlusLP/' /* webpackChunkName: "rewards_plus_lp" */));
        if(rewards_plus_lp){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <RewardsPlusLP />
                    </Suspense>
                </Provider>,
                rewards_plus_lp
            )
        }

        const wholesale_partner = document.getElementById('react-wholesale-partner');
        if(wholesale_partner){
            const WholesalePartner = React.lazy(() => import('./Wholesale' /* webpackChunkName: "wholesale_partner" */));
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <Router basename="/join-rebagforbusiness">
                            <WholesalePartner />
                        </Router>
                    </Suspense>
                </Provider>,
                wholesale_partner
            )
        }

        const bloomingdales_lp = document.getElementById('react-bloomingdales-lp');
        const BloomingdalesLP = React.lazy(() => import('./components/BloomingdalesLP/' /* webpackChunkName: "bloomingdales_lp" */));
        if(bloomingdales_lp){
            ReactDOM.render(
                <Provider store={store}>
                    <Suspense fallback={<div></div>}>
                        <BloomingdalesLP />
                    </Suspense>
                </Provider>,
                bloomingdales_lp
            )
        }

        const partner_lead_form = document.getElementById('react-partner-lead-form');
        if (partner_lead_form){
            const PartnerLeadForm = React.lazy(() => import('./components/PartnerLeadForm' /* webpackChunkName: "partner_lead_form" */));
            if(partner_lead_form){
                ReactDOM.render(
                    <Provider store={store}>
                        <Suspense fallback={<div></div>}>
                            <PartnerLeadForm />
                        </Suspense>
                    </Provider>,
                    partner_lead_form
                )
            }
        }

        const react_simplified_header = document.getElementById('react-simplified-header');
        if (react_simplified_header){
            const SimplifiedHeader = React.lazy(() => import('./SimplifiedHeader' /* webpackChunkName: "react_simplified_header" */));
            if(react_simplified_header){
                ReactDOM.render(
                  <Provider store={store}>
                      <Suspense fallback={<div></div>}>
                          <SimplifiedHeader app_mode={app_mode}/>
                      </Suspense>
                  </Provider>,
                  react_simplified_header
                )
            }
        }

        const continue_to_checkout = document.getElementById('react-continue-to-checkout');
        if (continue_to_checkout){
            const ContinuetoCheckout = React.lazy(() => import('./components/ContinuetoCheckout' /* webpackChunkName: "continue_to_checkout" */));
            ReactDOM.render(
              <Provider store={store}>
                  <Suspense fallback={<div></div>}>
                      <ContinuetoCheckout />
                  </Suspense>
              </Provider>,
              continue_to_checkout
            )
        }
        const for_you_plp = document.getElementById('react-for-you-plp');
        if (for_you_plp){
            const ForYouPLP = React.lazy(() => import('./components/ForYouPLP' /* webpackChunkName: "for_you_plp" */));
            ReactDOM.render(
              <Provider store={store}>
                  <Suspense fallback={<div></div>}>
                      <Router>
                          <ForYouPLP />
                      </Router>
                  </Suspense>
              </Provider>,
              for_you_plp
            )
        }
    });

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
